"use client";

import Image from "next/image";
import { motion } from "framer-motion";
import {
  Waves,
  Mountain,
  Music,
  ShieldCheck,
  Utensils,
  Users,
} from "lucide-react";
import { SITE } from "@/lib/constants";

const FEATURES = [
  { icon: Waves, text: "Pool with 12 Jacuzzi Points" },
  { icon: Mountain, text: "Panoramic Nandi Hills Views" },
  { icon: Music, text: "Pro Dance Floor & Audio" },
  { icon: ShieldCheck, text: "Private & Gated Estate" },
  { icon: Utensils, text: "Fully Equipped Kitchen" },
  { icon: Users, text: `Up to ${SITE.maxGuests} Guests` },
];

export function AboutSection() {
  return (
    <section id="about" className="section-padding bg-background">
      <div className="mx-auto max-w-7xl">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="relative aspect-[4/3] overflow-hidden rounded-2xl"
          >
            <Image
              src="/images/living-room-daytime.jpg"
              alt="Mountain Nest living room with Balinese interiors"
              fill
              className="object-cover"
              sizes="(max-width: 1024px) 100vw, 50vw"
            />
            <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/10" />
          </motion.div>

          {/* Text */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-primary">
              Discover
            </p>
            <h2 className="mt-3 font-playfair text-3xl font-bold leading-tight text-foreground md:text-4xl lg:text-5xl">
              About {SITE.name}
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground md:text-lg">
              <p>
                Nestled at the serene foothills of Nandi Hills, Mountain Nest is
                a luxury Balinese-inspired villa offering {SITE.sqft.toLocaleString()}{" "}
                sq ft of premium living across {SITE.floors} elegantly designed
                floors.
              </p>
              <p>
                Every detail has been curated to deliver an unparalleled
                experience — from the infinity swimming pool with 12 jacuzzi
                points to the panoramic sky deck overlooking the misty hills. 
                Whether it&apos;s a corporate retreat, a family celebration, or a
                romantic getaway, Mountain Nest transforms every occasion into an
                unforgettable memory.
              </p>
            </div>

            {/* Feature Grid */}
            <div className="mt-8 grid grid-cols-2 gap-4">
              {FEATURES.map((feat, i) => (
                <motion.div
                  key={feat.text}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-center gap-3 rounded-lg bg-card/60 px-3 py-3"
                >
                  <feat.icon className="size-5 shrink-0 text-primary" />
                  <span className="text-sm font-medium text-foreground">
                    {feat.text}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
